import type {DashboardTicket, DashboardTotals} from '../types/dashboardTicket';
import {isResolvedStatus} from '../../tickets/constants/statuses';
import {medianResolutionSeconds} from './medianResolution';

/** Median of a set of durations in seconds, or null when there are none. */
function median(values: readonly number[]): number | null {
	if (values.length === 0) return null;
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * The totals row's rollups (PRD §8.6) over the tickets in scope.
 *
 * Derived from the loaded rows rather than fetched, so the totals row and the group
 * headers above the same rows can never disagree: `resolvedRatio` and
 * `medianResolutionSeconds` here are the very functions `groupTickets` uses for a
 * bucket's subtotals.
 *
 * That also means it is a rollup **of what is loaded**. The bulk ticket query
 * (FOR-70) brings a server-side `ticketDashboardRollups` covering the whole
 * filtered set, which is what a paginated table needs; `DashboardTotals` is the
 * seam, so swapping the source is a change of one line in the route rather than a
 * change to the component.
 *
 * Nulls, not zeros, where there is nothing to compute from — a dash says "no
 * resolved tickets", where `0%` claims they were all missed.
 */
export function dashboardTotals(tickets: readonly DashboardTicket[]): DashboardTotals {
	if (tickets.length === 0) {
		return {
			total: 0,
			pct_resolved: null,
			sla_achievement_rate: null,
			median_resolution_time_seconds: null,
			median_first_response_time_seconds: null,
		};
	}

	const resolved = tickets.filter(ticket => isResolvedStatus(ticket.status));

	// Scoped to resolved tickets, per the formula: a ticket still in the queue has
	// not yet missed its SLA, so counting it as a failure would make the rate fall
	// simply because new work arrived.
	const withSla = resolved.filter(ticket => ticket.sla != null);
	const achieved = withSla.filter(ticket => !ticket.sla!.breached).length;

	const firstResponses = tickets
		.map(ticket => ticket.first_response_time_seconds)
		.filter((seconds): seconds is number => seconds != null)
		.map(seconds => Math.max(0, seconds));

	return {
		total: tickets.length,
		pct_resolved: resolved.length / tickets.length,
		sla_achievement_rate: withSla.length > 0 ? achieved / withSla.length : null,
		median_resolution_time_seconds: medianResolutionSeconds(resolved),
		median_first_response_time_seconds: median(firstResponses),
	};
}
